import { useContext, useEffect, useState } from "react";
import Button from "../../utils/components/Button";
import SelectBox from "../../utils/components/SelectBox";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";
import API from "../../utils/API";
import { useProject } from "../../utils/hooks/useProject";
import { auth } from "../../App/routes";
import { confirmAlert } from "react-confirm-alert";
import AlertModal from "../../utils/components/AlertModal";
import { AiOutlineClose, AiOutlineFilePdf } from "react-icons/ai";
import { BiEditAlt } from "react-icons/bi";
import { FiLock } from "react-icons/fi";
import TaskCancelModal from "../Forms/components/TaskCancelModal";
import { Translate } from "react-localize-redux";
import NewTaskAssignmentModal from "../Forms/components/NewTaskAssignmentModal";
import WorkflowForwardModal from "./components/WorkflowForwardModal";
import WorkflowTaskCancelModal from "./components/WorkflowTaskCancelModal";
import timeStampToDateFormat from "../../utils/helpers/timestampToDateFormat";
import TaskDelegateModal from "./components/TaskDelegateModal";
import Loading from "../../utils/components/Loading";
import toastr from "toastr";
import { useCan } from "../../utils/hooks/useCan";
import formatSubFormData from "../../utils/helpers/formatSubFormData";
import formatSavedSubForm from "../../utils/helpers/formatSavedSubForm";
import { RequestContext } from "../../utils/context/RequestContext";
import getLocation from "../../utils/helpers/getLocation";
import Input from "../../utils/components/Input";

const FORMTYPES = {
  TASK: "task",
  WORKFLOW: "workflow",
};

const TasksDetail = ({ user, settings, translate }) => {
  const [isReadOnly, setIsReadOnly] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const [editedTaskInfo, setEditedTaskInfo] = useState({});
  const [taskInfo, setTaskInfo] = useState({});
  const [selectedLocation, setSelectedLocation] = useState("");
  const [statusSets, setStatusSets] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedTaskStatus, setSelectedTaskStatus] = useState([]);
  const [subForms, setSubForms] = useState([]);
  const [editedSubForms, setEditedSubForms] = useState([]);
  const [taskUsers, setTaskUsers] = useState([]);
  const [taskCompanies, setTaskCompanies] = useState([]);
  const [taskGroups, setTaskGroups] = useState([]);
  const [formInfo, setFormInfo] = useState({});
  const [workflow, setWorkflow] = useState({});
  const [isLock, setIsLock] = useState(false);
  const [dueDate, setDueDate] = useState(null);
  const [statusInfo, setStatusInfo] = useState("");
  const [isNewTaskAssignmentModalOpen, setIsNewTaskAssignmentModalOpen] =
    useState(false);
  const [formType, setFormType] = useState(FORMTYPES.TASK);
  const [isWorkflowForwardModalOpen, setIsWorkflowForwardModalOpen] =
    useState(false);
  const [isTaskDelegateModalOpen, setIsTaskDelegateModalOpen] = useState(false);
  const [isWorkflowTaskCancelModalOpen, setIsWorkflowTaskCancelModalOpen] =
    useState(false);

  const [isTaskCancelModalOpen, setIsTaskCancelModalOpen] = useState(false);

  const [attachments, setAttachments] = useState([]);
  const [isAttachmentsUpdated, setIsAttachmentsUpdated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingWorkflowForward, setIsSendingWorkflowForward] =
    useState(false);

  const [isSavingForm, setIsSavingForm] = useState(false);

  const [canBeDelegated, setCanBeDelegated] = useState(false);
  const [shouldAskLocationAccess, setShouldAskLocationAccess] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [generalInformations, setGeneralInformations] = useState({
    formName: "",
    revision: "",
    metadata: [],
  });
  const [selectedWorkflowStatus, setSelectedWorkflowStatus] = useState(null);

  const { id } = useParams();
  const location = useLocation();
  const { project } = useProject();
  const navigate = useNavigate();

  const [isCurrentUser, setIsCurrentUser] = useState(false);

  const can = useCan();

  const { setRequestLoading } = useContext(RequestContext);

  useEffect(() => {
    if (formInfo && Object.keys(formInfo).length > 0) {
      setGeneralInformations({
        formName: {
          value: formInfo.name,
          label: "form_name",
        },
        revision: {
          value: formInfo.revisionSetDetailName,
          label: "revision",
        },
        metadata: formInfo.metadata,
      });
    }
  }, [formInfo]);

  useEffect(() => {
    const localOldForm = JSON.parse(localStorage.getItem("old_form"));

    if (localOldForm) {
      setStatusSets(localOldForm.statusSets);
      setSelectedStatus(localOldForm.selectedStatus);
      setTaskInfo(localOldForm.taskInfo);
      setEditedTaskInfo(localOldForm.editedTaskInfo);
      setFormInfo(localOldForm.formInfo);
      setSubForms(localOldForm.subForms);
      setEditedSubForms(localOldForm.editedSubForms);
      setSelectedLocation(localOldForm.selectedLocation);
      setAttachments(localOldForm.attachments);
      setIsAttachmentsUpdated(localOldForm.isAttachmentsUpdated);
      setFormType(localOldForm.formType);
      setDueDate(localOldForm.dueDate);
      setWorkflow(localOldForm.workflow);
      setSelectedTaskStatus(localOldForm.selectedTaskStatus);
      setIsCurrentUser(localOldForm.isCurrentUser);
      setIsReadOnly(false);

      localStorage.removeItem("old_form");
    } else {
      setIsLoading(true);
      const url = `task/${id}`;
      API.getData(url)
        .then(({ data: res }) => {
          const { data } = res;

          const taskInfo = {
            id: data.id,
          };

          const formMetadata = data.form.headers.map((header) => ({
            id: header.set_id,
            name: header.set_name,
            data: header.details.map((data) => ({
              id: data.id,
              label: data.name,
            })),
          }));

          const formInfo = {
            id: data.form.id,
            name: data.form.name,
            no: data.form.no,
            revisionSetDetailName: data.form.revision_set_detail_name,
            metadata: formMetadata,
          };

          setCanBeDelegated(data.is_delegable);

          setSelectedLocation(data.form.site_set);
          setTaskInfo(taskInfo);
          setFormInfo(formInfo);
          setEditedTaskInfo(taskInfo);
          setDueDate(data.due_date);
          setStatusInfo(data.status);

          if (data.workflow) {
            setFormType(FORMTYPES.WORKFLOW);
            setWorkflow(data.workflow);
          }

          setStatusSets(
            data.form.status_set
              .filter((status) => !status.locked)
              .map((status) => ({
                value: status.id,
                label: status.name,
                isDisabled: status.locked,
                isSelected: status.selected,
              })),
          );

          const selectedStatus = data.form.status_set.find(
            (status) => status.selected,
          );

          if (selectedStatus) {
            setSelectedTaskStatus({
              value: selectedStatus.id,
              label: selectedStatus.name,
            });
          }

          if (res.data.form.attachments)
            setAttachments(
              res.data.form.attachments.map((attachment) => ({
                url: attachment.file_path,
                name: attachment.file_name,
                uploadedBy: attachment.created_name,
                uploadedAt: timeStampToDateFormat(attachment.created_at),
                id: attachment.id,
              })),
            );

          const newSubForm = formatSavedSubForm(
            data.form.sub_forms,
            null,
            setShouldAskLocationAccess,
          );

          setSubForms(JSON.parse(JSON.stringify(newSubForm)));
          setEditedSubForms(JSON.parse(JSON.stringify(newSubForm)));

          // Check backend flags for readonly mode
          const isInfoUser = data.is_info_user || false;
          const isReadOnlyFromBackend = data.is_readonly || false;

          // Set readonly if: backend says readonly, or user is info user, or user is not the receiver
          if (isReadOnlyFromBackend || isInfoUser ||
              (data.receiver && data.receiver.id !== user.id)) {
            setIsReadOnly(true);
            setIsCurrentUser(false);
          } else if (data.receiver && data.receiver.id === user.id) {
            setIsCurrentUser(true);
            setIsReadOnly(false);
          } else {
            setIsReadOnly(true);
          }
          setIsLoading(false);
        })
        .then(() => {
          setIsSavingForm(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setEditedTaskInfo(taskInfo);
  }, [taskInfo, isReadOnly, isCurrentUser]);

  useEffect(() => {
    if (shouldAskLocationAccess && !userLocation && !window.chrome) {
      getLocation((location) => setUserLocation(location));
    }
  }, [shouldAskLocationAccess, userLocation]);

  useEffect(() => {
    if (project && taskUsers.length < 1 && !isReadOnly)
      API.getData(`task/initialize?project_id=${project?.id}`).then(
        ({ data: res }) => {
          setTaskUsers(
            res.data.users.map((user) => ({
              isInfoUser: user.is_info_user,
              isActionUser: user.is_action_user,
              value: user.user_id,
              label: user.user_name,
              secLabel: user.company_name,
            })),
          );

          setTaskCompanies(
            res.data.companies.map((company) => ({
              value: company.id,
              label: company.name,
              users: company.users,
            })),
          );

          setTaskGroups(
            res.data.groups.map((group) => ({
              value: group.id,
              label: group.name,
              users: group.users.map((user) => ({
                ...user,
                isActionUser: user.is_action_user,
                isInfoUser: user.is_info_user,
              })),
              secLabel:
                group.users.length > 0 ? group.users[0].company_name : "",
            })),
          );
        },
      );
  }, [isReadOnly, project, taskUsers.length]);

  const handleCloseForm = () => {
    if (location.state?.from) window.history.back();
    else navigate(auth.tasks.link);
  };

  const handleAddRemoveRepeatableForm = (
    type,
    index,
    subFormIndex,
    repeatableIndex,
  ) => {
    if (type === "add") {
      const newSubForms = [...editedSubForms];
      const newRepeatableFormFields = newSubForms[subFormIndex].fields[
        index
        ].fields[0].map((field) => ({
        ...field,
        value: field.default_value,
        field_attach: {
          image: "",
          note: "",
          original_forms: [],
        },
      }));

      newSubForms[subFormIndex].fields[index].fields.push(
        newRepeatableFormFields,
      );

      setEditedSubForms(newSubForms);
    } else if (type === "remove") {
      const newSubForms = [...editedSubForms];

      newSubForms[subFormIndex].fields[index].fields.splice(repeatableIndex, 1);

      setEditedSubForms(newSubForms);
    }
  };

  const handleUpdateFormField = (
    value,
    formIndex,
    fieldIndex,
    repeatableIndex,
    repeatableFieldIndex,
  ) => {
    const newSubForms = [...editedSubForms];

    if (repeatableIndex !== null) {
      if (newSubForms[formIndex]?.fields[fieldIndex]?.fields[repeatableIndex]) {
        newSubForms[formIndex].fields[fieldIndex].fields[repeatableIndex][
          repeatableFieldIndex
          ].value = value;

        setEditedSubForms([...newSubForms]);
      }
    } else {
      if (newSubForms[formIndex]?.fields[fieldIndex]) {
        newSubForms[formIndex].fields[fieldIndex].value = value;

        setEditedSubForms([...newSubForms]);
      }
    }
  };

  const handleSaveForm = (e, sendData = false, type = null) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (sendData && !type) return;

    const isFormUpdated =
      JSON.stringify(subForms) !== JSON.stringify(editedSubForms);

    const isStatusUpdated =
      (formType === FORMTYPES.WORKFLOW && selectedWorkflowStatus) ||
      (formType === FORMTYPES.TASK &&
        selectedStatus?.value !==
        statusSets?.find((status) => status.isSelected)?.value);

    if (isFormUpdated || isStatusUpdated || isAttachmentsUpdated || !selectedStatus?.value) {
      if (
        (formType === FORMTYPES.WORKFLOW && !selectedWorkflowStatus) ||
        !selectedStatus?.value
      ) {
        const message = translate("forms.status_empty_alert");

        confirmAlert({
          customUI: ({ onClose }) => (
            <AlertModal
              buttons={[
                {
                  label: translate("okay"),
                  onClick: () => onClose(),
                  classNames: "btn-primary",
                },
              ]}
              message={message}
            />
          ),
        });
        return;
      }

      const formData = {
        status_set_detail_id: selectedStatus?.value,
        sub_forms: formatSubFormData(editedSubForms),
      };

      setIsSavingForm(true);
      setRequestLoading(true);

      API.putData(
        `form/${formInfo.id}?project_id=${project?.id}&task_id=${taskInfo.id}${
          formType === FORMTYPES.WORKFLOW
            ? `&step_status=${
              sendData?.status === "Kabul" ? "accept" : "reject"
            } `
            : ``
        }`,
        formData,
      )
        .then(({ data: res }) => {
          // If we're creating a task/workflow, keep loading active
          if (sendData) {
            setRequestLoading(true);
          }

          if (isAttachmentsUpdated && attachments.length) {
            API.putData(
              `form_attachment/${res.data.id}?project_id=${project?.id}?task_id=${taskInfo.id}`,
              {
                files: attachments
                  .filter((attachment) => attachment.base64)
                  .map((attachment) => ({
                    file_data: attachment.base64,
                    file_name: attachment.name,
                  })),
                prev_form_id: formInfo.id,
              },
            )
              .then(() => {
                handlePostSubmit(sendData, res.data.id, type);
              })
              .catch(() => {
                setIsSavingForm(false);
                setRequestLoading(false);
              });
          } else {
            handlePostSubmit(sendData, res.data.id, type);
          }

          localStorage.removeItem("nested_form");
        })
        .catch(() => {
          setIsSavingForm(false);
          setRequestLoading(false);
        });
    } else {
      if (isAttachmentsUpdated && attachments.length) {
        API.putData(
          `form_attachment/${formInfo.id}?project_id=${project?.id}?task_id=${taskInfo.id}`,
          {
            files: attachments
              .filter((attachment) => attachment.base64)
              .map((attachment) => ({
                file_data: attachment.base64,
                file_name: attachment.name,
              })),
            prev_form_id: formInfo.id,
          },
        ).catch(() => {});
      }
      if (type !== "workflow-forward") {
        setIsSavingForm(true);
        setRequestLoading(true);

        handlePostSubmit(sendData, formInfo.id, type);
      } else {
        setIsReadOnly(!isReadOnly);
      }
    }
  };

  const handlePostSubmit = (sendData, id, type) => {
    if (!sendData) {
      setRequestLoading(false);
      navigate(auth.formDetails.link.replace(":id", id));
    } else {
      if (type === FORMTYPES.TASK) {
        sendData.form_id = id;
        API.postData("tasks", sendData)
          .then(({ data: res }) => {
            setRequestLoading(false);
            navigate(auth.formBoxDetails.link.replace(":id", res.data.id));
          })
          .catch(() => {
            setRequestLoading(false);
          });
      } else if (type === "workflow-forward") {
        setIsSendingWorkflowForward(true);
        API.postData("workflow/forward", sendData)
          .then(({ data: res }) => {
            setRequestLoading(false);
            toastr.success(res.message);
            navigate(auth.tasks.link);

            setIsSendingWorkflowForward(false);
          })
          .catch(() => {
            setIsSavingForm(false);
            setIsSendingWorkflowForward(false);
            setRequestLoading(false);
          });
      } else if (type === "delegate") {
        setIsSendingWorkflowForward(true);
        API.postData("/workflow/delegate", sendData)
          .then(() => {
            setRequestLoading(false);
            toastr.success(
              translate("tasks.task_delegate.task_delegate_success"),
            );
            navigate(auth.tasks.link);

            setIsSendingWorkflowForward(false);
          })
          .catch((err) => {
            toastr.error(translate("tasks.task_delegate.task_delegate_error"));
            console.log(err);

            setIsSendingWorkflowForward(false);
            setRequestLoading(false);
          });
      }
    }
  };

  const handleUpdateFormFieldAttachment = (
    value,
    formIndex,
    fieldIndex,
    repeatableIndex,
    repeatableFieldIndex,
    attachmentType,
  ) => {
    const newSubForms = [...subForms];

    if (repeatableIndex !== null) {
      newSubForms[formIndex].fields[fieldIndex].fields[repeatableIndex][
        repeatableFieldIndex
        ].field_attach[attachmentType] = value;

      setEditedSubForms(newSubForms);
    } else {
      newSubForms[formIndex].fields[fieldIndex].field_attach[attachmentType] =
        value;

      setEditedSubForms(newSubForms);
    }
  };

  const handleKeepOldForm = () => {
    const oldForm = {
      statusSets,
      selectedStatus,
      taskInfo,
      editedTaskInfo,
      formInfo,
      subForms,
      editedSubForms,
      selectedLocation,
      attachments,
      isAttachmentsUpdated,
      formType,
      dueDate,
      workflow,
      selectedTaskStatus,
      isCurrentUser,
    };

    localStorage.setItem("old_form", JSON.stringify(oldForm));
  };

  return isLoading ? (
    <Loading text={translate("loading") + `...`}/>
  ) : (
    <div className="TaskDetail task-page">
      <div className="container bg-white shadow-sm border border-1 rounded">
        {/** Form actions */}
        <div
          className={`row flex-column-reverse flex-lg-row mt-4 pb-4 border-bottom border-2 ${
            isReadOnly ? "d-none" : "d-flex"
          }`}
        >
          <div className="col-12 col-lg-2">
            <Button
              className="btn-secondary px-5 ms-0 ms-lg-4 mt-2 mt-lg-0"
              onClick={() => {
                setIsReadOnly(!isReadOnly);
              }}
              disabled={isSavingForm}
            >
              {translate("cancel")}
            </Button>
          </div>
          <div className="col-12 col-lg-4 mt-2 mt-lg-0">
            <SelectBox
              options={
                formType === FORMTYPES.WORKFLOW
                  ? workflow?.statuses?.map(({ status }) => ({
                    value: status,
                    label: status,
                  }))
                  : statusSets
              }
              placeholder={translate("forms.place-holders.select-status")}
              value={
                formType === FORMTYPES.WORKFLOW
                  ? selectedWorkflowStatus || undefined
                  : selectedStatus
              }
              onChange={(selectedStatus) => {
                formType === FORMTYPES.WORKFLOW
                  ? setSelectedWorkflowStatus(selectedStatus)
                  : setSelectedStatus(selectedStatus);
              }}
              disabled={isReadOnly || isSavingForm}
            />
          </div>
          <div
            className="col-12 col-lg-6 d-flex flex-wrap flex-lg-nowrap justify-content-between justify-content-lg-end">
            {statusInfo !== "StatusType.delegated" &&
              canBeDelegated &&
              can.doThat("task:delegate") && (
                <Button
                  className="btn-orange text-white px-5 me-0 me-md-2 me-lg-3 mb-3 mb-lg-0"
                  onClick={() => {
                    setIsTaskDelegateModalOpen(true);
                  }}
                  disabled={isSavingForm}
                >
                  {translate("tasks.task_delegate.set_delegate")}
                </Button>
              )}
            {can.doThat("form:update") && (
              <Button
                className="btn-orange text-white px-5 mb-3 mb-lg-0"
                onClick={handleSaveForm}
                disabled={isSavingForm}
              >
                {translate("save")}
              </Button>
            )}
            {can.doThat("task:create") && (
              <Button
                className="btn-orange text-white px-5 ms-0 ms-md-2 ms-lg-3 mb-3 mb-lg-0"
                onClick={() => {
                  if (formType === FORMTYPES.TASK)
                    setIsNewTaskAssignmentModalOpen(
                      !isNewTaskAssignmentModalOpen,
                    );
                  else {
                    const payload = {
                      task_id: taskInfo.id,
                      status: selectedWorkflowStatus
                        ? selectedWorkflowStatus.label
                        : "",
                      step_workers: [],
                    };
                    handleSaveForm(null, payload, "workflow-forward");
                  }
                }}
                disabled={isSavingForm}
              >
                {translate("send")}
              </Button>
            )}
            {can.doThat("task:create") && (
              <Translate>
                {({ translate }) => (
                  <NewTaskAssignmentModal
                    show={isNewTaskAssignmentModalOpen}
                    hideModal={(action) => {
                      if (action === "send") {
                        setIsReadOnly(!isReadOnly);
                        setIsLock(!isLock);
                      }
                      setIsNewTaskAssignmentModalOpen(
                        !isNewTaskAssignmentModalOpen,
                      );
                    }}
                    users={taskUsers}
                    companies={taskCompanies}
                    groups={taskGroups}
                    formId={parseInt(id)}
                    translate={translate}
                    handleSaveForm={(sendData, type) =>
                      handleSaveForm(null, sendData, type)
                    }
                  />
                )}
              </Translate>
            )}
            <WorkflowForwardModal
              show={isWorkflowForwardModalOpen}
              hideModal={() =>
                setIsWorkflowForwardModalOpen(!isWorkflowForwardModalOpen)
              }
              translate={translate}
              handleSaveForm={(sendData, type) =>
                handleSaveForm(null, sendData, type)
              }
              taskId={taskInfo.id}
              workflow={workflow}
              users={taskUsers}
              isSendingFromParent={isSendingWorkflowForward}
              isSendingForm={isSavingForm}
            />
            {can.doThat("task:delegate") && (
              <TaskDelegateModal
                show={isTaskDelegateModalOpen}
                hideModal={() =>
                  setIsTaskDelegateModalOpen(!isTaskDelegateModalOpen)
                }
                translate={translate}
                users={taskUsers}
                companies={taskCompanies}
                groups={taskGroups}
                taskId={taskInfo.id}
                handleSaveForm={(sendData, type) =>
                  handleSaveForm(null, sendData, type)
                }
              />
            )}
          </div>
        </div>
        {/** Form actions */}
        {can.doThat("task:cancel") && (
          <WorkflowTaskCancelModal
            show={isWorkflowTaskCancelModalOpen}
            taskId={taskInfo.id}
            hideModal={() =>
              setIsWorkflowTaskCancelModalOpen(!isWorkflowTaskCancelModalOpen)
            }
            statusSets={statusSets}
            dueDate={dueDate}
            translate={translate}
            formName={formInfo?.name}
            formNo={formInfo?.no}
          />
        )}
        {/** Form Edit */}
        <div
          className={`row flex-column-reverse flex-lg-row align-items-center justify-content-end mt-4 pb-4 border-bottom border-2
  ${isReadOnly ? "d-flex" : "d-none"}`}
        >
          <div className="col-12 col-lg-4 mt-2 mt-lg-0">
            <p className="fs-3 text-orange m-0">{formInfo?.name}</p>
          </div>
          <div className="col-12 col-lg-4 mt-2 mt-lg-0 d-flex flex-column align-items-center">
            <p className="fs-4 fw-bold mb-2">{formInfo?.no}</p>
            <p className="fs-4 border border-2 p-2 rounded text-center mb-0">
              {selectedStatus?.label}
            </p>
          </div>
          <div
            className={`col-12 col-lg-4 d-flex justify-content-end align-items-center ${
              isLock ? "d-none" : "d-flex"
            }`}
          >
            <Button
              className="border-0 bg-transparent shadow-none p-0 mx-2"
              onClick={() => {
                confirmAlert({
                  customUI: ({ onClose: onClick }) => (
                    <AlertModal
                      buttons={[
                        {
                          label: "Okay",
                          onClick,
                          classNames: "btn btn-primary",
                        },
                      ]}
                      message="PDF dosyası oluşacak..."
                    />
                  ),
                });
              }}
            >
              <AiOutlineFilePdf className="text-red fs-3"/>
            </Button>
            {/* Edit button - hide for info users (readonly from backend flags) */}
            {!isReadOnly && (
              <Button
                className="border-0 bg-transparent shadow-none p-0 mx-2"
                onClick={() => {
                  setIsReadOnly(!isReadOnly);
                }}
              >
                <BiEditAlt className="fs-2 fw-bold"/>
              </Button>
            )}
            <Button
              className="border-0 bg-transparent shadow-none p-0 mx-2"
              onClick={handleCloseForm}
            >
              <AiOutlineClose className="fs-4 fw-bold"/>
            </Button>
            {formType === FORMTYPES.WORKFLOW && can.doThat("task:cancel") && (
              <Button
                className="btn btn-orange text-white shadow-none px-3 mx-2"
                onClick={() => {
                  setIsWorkflowTaskCancelModalOpen(
                    !isWorkflowTaskCancelModalOpen,
                  );
                }}
              >
                Workflow'u iptal et
              </Button>
            )}
          </div>
          <div
            className={`col-12 col-lg-4 align-items-center justify-content-end ${
              isLock ? "d-flex" : "d-none"
            }`}
          >
            <FiLock
              className="display-5 me-3"
              onClick={() => setIsTaskCancelModalOpen(!isTaskCancelModalOpen)}
            />
          </div>
          <TaskCancelModal
            show={isTaskCancelModalOpen}
            hideModal={() => {
              setIsTaskCancelModalOpen(!isTaskCancelModalOpen);
            }}
            cancelAction={() => {
              setIsTaskCancelModalOpen(!isTaskCancelModalOpen);
              setIsLock(!isLock);
            }}
          />
        </div>
        {/** Form Edit */}
        {!isReadOnly && (
          <div className="d-flex justify-content-between align-items-center mt-4 ps-3 pe-3 border-bottom border-2 pb-3">
            <div className="fs-4 text-orange">{formInfo?.name}</div>
            <div className="fs-4 text-black">{selectedTaskStatus?.label}</div>
            <div className="fs-4 text-black">{formInfo?.no}</div>
          </div>
        )}

        {/** General Information */}
        <div className="row pt-3 pb-5 mt-4 ps-5 general-informations-section">
          <div className="col-12">
            <p className="fs-5 text-orange">
              {translate("forms.headers.general-informations")}
            </p>
          </div>

          <div className="d-flex flex-column gap-2">
            {generalInformations &&
              Object.keys(generalInformations).length > 0 &&
              Object.keys(generalInformations).map((key, keyIndex) =>
                key !== "metadata" ? (
                  <div key={keyIndex} className="col-12 col-lg-10">
                    <div className="row align-items-center">
                      <p className="col-12 col-lg-2 mb-0 fw-bold">
                        {translate(
                          `forms.headers.${generalInformations[key].label}`,
                        )}
                      </p>
                      <div className="col-12 col-lg-10">
                        <Input
                          disabled={true}
                          placeholder={generalInformations[key].value}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  generalInformations[key].map((meta) => (
                    <div key={meta.id} className="col-12 col-lg-10">
                      <div className="row align-items-center">
                        <p className="col-12 col-lg-2 mb-0 fw-bold">
                          {meta.name}
                        </p>
                        <div className="col-12 col-lg-10">
                          <Input
                            disabled={true}
                            placeholder={meta.data
                              .map((data) => data.label)
                              .join(", ")}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                ),
              )}
          </div>
        </div>
        {/** General Information */}

        <div className="inner-page mb-5">
          <Outlet
            context={{
              isReadOnly,
              taskInfo,
              subForms,
              editedSubForms,
              settings,
              selectedLocation,
              handleAddRemoveRepeatableForm,
              handleUpdateFormField,
              attachments,
              setAttachments,
              setIsAttachmentsUpdated,
              handleUpdateFormFieldAttachment,
              handleKeepOldForm,
              translate,
            }}
          />
        </div>
      </div>
    </div>
  );
};
export default TasksDetail;
